/**
 * Super admin — εργαλεία ανά οργανισμό που ΔΕΝ βλέπουν πια οι χρήστες οργανισμού (απόφαση ιδιοκτήτη 15/10/2026):
 * «Εκδόσεις agent» (αναβάθμιση print agent στα ταμεία) και «Κατάλογος πλατφορμών». Ίδιες σελίδες με τα
 * κεντρικά (OrgPrintAgentFleet, OrgPlatformCatalogTab), με επιλογή οργανισμού από πάνω. Τα endpoints δέχονται
 * τον super admin ως ιδιοκτήτη (requireOrgAccess)· τα fleet/rollout είναι πλέον ΜΟΝΟ γι' αυτόν.
 *
 * 24/10/2026: οι «Εκδόσεις agent» έχουν και την επιλογή «Χωρίς οργανισμό» — καταστήματα με organization_id NULL
 * (στον 144: 1, 2, 4, 27, 32, 34 και 35 GENERIS) που ως τότε δεν φαίνονταν πουθενά. Ο κατάλογος πλατφορμών δεν
 * την έχει: χωρίς οργανισμό δεν υπάρχει κεντρικός κατάλογος.
 */
import React, { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { organizationAPI } from '../../services/api';
import { Notice, SELECT_CLS } from '../shared/listPage';
import OrgPrintAgentFleet from '../OrgPrintAgentFleet';
import OrgPlatformCatalogTab from '../OrgPlatformCatalogTab';

export type SuperAdminOrgTool = 'agent-releases' | 'platform-catalog';

const STORAGE_KEY = 'superadmin_tools_org';
// Τιμή της λίστας για «Χωρίς οργανισμό» — μόνο στις «Εκδόσεις agent».
const NO_ORG = 'none';
type Choice = number | typeof NO_ORG;

const readStoredOrg = (): Choice | null => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === NO_ORG) return NO_ORG;
    const v = Number(raw);
    return Number.isInteger(v) && v > 0 ? v : null;
  } catch {
    return null;
  }
};

const SuperAdminOrgTools: React.FC<{ tool: SuperAdminOrgTool }> = ({ tool }) => {
  const { t } = useTranslation(['superAdminPanel']);
  const uid = useId();
  const [orgs, setOrgs] = useState<Array<{ id: number; name: string }> | null>(null);
  const [error, setError] = useState(false);
  const [orgId, setOrgId] = useState<Choice | null>(readStoredOrg);
  const allowNoOrg = tool === 'agent-releases';

  useEffect(() => {
    let alive = true;
    organizationAPI.list()
      .then((d: { organizations?: Array<{ id: number; name: string }> }) => {
        if (!alive) return;
        const list = (d.organizations || []).map(o => ({ id: o.id, name: o.name }));
        setOrgs(list);
        setOrgId(prev => {
          if (prev === NO_ORG) return allowNoOrg ? NO_ORG : list[0]?.id ?? null;
          if (prev && list.some(o => o.id === prev)) return prev;
          return list[0]?.id ?? (allowNoOrg ? NO_ORG : null);
        });
      })
      .catch(() => { if (alive) setError(true); });
    return () => { alive = false; };
  }, [allowNoOrg]);

  const choose = (raw: string) => {
    const next: Choice = raw === NO_ORG ? NO_ORG : Number(raw);
    setOrgId(next);
    try { localStorage.setItem(STORAGE_KEY, String(next)); } catch { /* μόνο ευκολία */ }
  };

  if (error) return <Notice tone="danger" testId="superadmin-org-tools-error">{t('superAdminPanel:orgTools.loadFailed')}</Notice>;
  if (!orgs) {
    return (
      <div className="flex items-center gap-2 text-t3 p-6">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
        {t('superAdminPanel:orgTools.loading')}
      </div>
    );
  }
  if (orgId === null || (orgs.length === 0 && orgId !== NO_ORG)) return <Notice tone="warn">{t('superAdminPanel:orgTools.noOrgs')}</Notice>;

  return (
    <div data-testid={`superadmin-${tool}`}>
      <div className="mb-4 flex flex-wrap items-center gap-3 rounded-xl border border-line bg-surface-2 px-4 py-3">
        <label htmlFor={`${uid}-org`} className="text-sm font-semibold text-t2">{t('superAdminPanel:orgTools.org')}</label>
        <select id={`${uid}-org`} value={String(orgId)} onChange={e => choose(e.target.value)}
          className={`${SELECT_CLS} max-w-xs`} data-testid="superadmin-org-tools-select">
          {orgs.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
          {allowNoOrg && <option value={NO_ORG}>{t('superAdminPanel:orgTools.noOrg')}</option>}
        </select>
        <span className="text-sm text-t3">{t(`superAdminPanel:orgTools.hint.${tool}`)}</span>
      </div>
      {tool === 'agent-releases'
        ? <OrgPrintAgentFleet key={String(orgId)} orgId={orgId === NO_ORG ? null : orgId} />
        : orgId !== NO_ORG && <OrgPlatformCatalogTab key={orgId} orgId={orgId} canManage canEdit canOwn />}
    </div>
  );
};

export default SuperAdminOrgTools;
